/**
 * useSidebarLayout — Per-user sidebar categories + channel assignments.
 *
 * Loads /me/sidebar on mount, exposes CRUD ops for categories and a
 * bulk syncLayout() for drag-and-drop results.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./useAuth";

export interface SidebarCategory {
  id: string;
  name: string;
  sortOrder: number;
  isExpanded: boolean;
}

export interface SidebarAssignment {
  categoryId: string;
  channelId: string;
  sortOrder: number;
}

interface LayoutResponse {
  categories: SidebarCategory[];
  assignments: SidebarAssignment[];
}

export function useSidebarLayout() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const [categories, setCategories] = useState<SidebarCategory[]>([]);
  const [assignments, setAssignments] = useState<SidebarAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const data = await api.get<LayoutResponse>("/me/sidebar");
      setCategories(data.categories);
      setAssignments(data.assignments);
    } catch (err) {
      console.error("[sidebar-layout] load failed", err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { void load(); }, [load]);

  const createCategory = useCallback(async (name: string): Promise<SidebarCategory | null> => {
    try {
      const created = await api.post<SidebarCategory>("/me/sidebar/categories", { name });
      setCategories((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
      return created;
    } catch (err) {
      console.error("[sidebar-layout] create failed", err);
      return null;
    }
  }, []);

  const renameCategory = useCallback(async (id: string, name: string) => {
    // Optimistic
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
    try {
      await api.patch(`/me/sidebar/categories/${id}`, { name });
    } catch (err) {
      console.error("[sidebar-layout] rename failed", err);
      await load(); // reload to recover
    }
  }, [load]);

  const deleteCategory = useCallback(async (id: string) => {
    // Optimistic
    setCategories((prev) => prev.filter((c) => c.id !== id));
    setAssignments((prev) => prev.filter((a) => a.categoryId !== id));
    try {
      await api.delete(`/me/sidebar/categories/${id}`);
    } catch (err) {
      console.error("[sidebar-layout] delete failed", err);
      await load();
    }
  }, [load]);

  const toggleExpanded = useCallback(async (id: string) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    const nextExpanded = !cat.isExpanded;
    // Optimistic
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, isExpanded: nextExpanded } : c)));
    try {
      await api.patch(`/me/sidebar/categories/${id}`, { isExpanded: nextExpanded });
    } catch (err) {
      console.error("[sidebar-layout] toggle failed", err);
      await load();
    }
  }, [categories, load]);

  /**
   * Bulk-sync after drag-and-drop.
   * nextCategories: ordered list (sort_order = index)
   * nextAssignments: full set of assignments that should exist (categoryId !== null = in a category).
   *                  Channels omitted entirely fall back to "uncategorized".
   */
  const syncLayout = useCallback(async (
    nextCategories: Array<{ id: string; sortOrder: number }>,
    nextAssignments: Array<{ channelId: string; categoryId: string | null; sortOrder: number }>,
  ) => {
    // Optimistic: apply locally first
    setCategories((prev) => {
      const orderMap = new Map(nextCategories.map((c) => [c.id, c.sortOrder]));
      return prev
        .map((c) => ({ ...c, sortOrder: orderMap.get(c.id) ?? c.sortOrder }))
        .sort((a, b) => a.sortOrder - b.sortOrder);
    });
    setAssignments(
      nextAssignments
        .filter((a): a is { channelId: string; categoryId: string; sortOrder: number } => a.categoryId !== null)
        .map((a) => ({ categoryId: a.categoryId, channelId: a.channelId, sortOrder: a.sortOrder })),
    );

    try {
      await api.put("/me/sidebar/layout", {
        categories: nextCategories,
        assignments: nextAssignments,
      });
    } catch (err) {
      console.error("[sidebar-layout] sync failed", err);
      await load(); // rollback on error
    }
  }, [load]);

  return {
    categories,
    assignments,
    isLoading,
    createCategory,
    renameCategory,
    deleteCategory,
    toggleExpanded,
    syncLayout,
    reload: load,
  };
}
